import { Field, Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { singUpRequest } from '../api';
import { signUpSchema } from '../schema';

import toast from 'react-hot-toast';
import Button from '../../../shared/ui/button/button';

import { FieldError } from './field-error/field-error';
import { FieldPassword } from './field-password/field-password';

import { MfaModal } from './mfa-modal';
import { useMfaVerification } from '../model/use-mfa-verification';

import { FirebaseGoogleSignIn } from '../../../components/googleSingIn/googleSingIn';

import {
	getApiErrorMessage,
	isNetworkError,
} from '../../../shared/api/apiError';

import styles from './sign-up-form.module.css';

export function SignUpForm() {
	const intl = useIntl();
	const navigate = useNavigate();
	const mfa = useMfaVerification();
	const t = id => intl.formatMessage({ id });

	const handleSubmit = async (values, { resetForm, setStatus }) => {
		setStatus(null);

		const result = await singUpRequest({
			full_name: values.full_name,
			email: values.email,
			password: values.password,
			user_type: 'individual',
			language_preference: 'hy',
			currency_preference: 'AMD',
		});

		if (isNetworkError(result)) {
			setStatus(t('SignInValidationId5'));
			return;
		}

		if (result instanceof Error) {
			setStatus(getApiErrorMessage(result, t('SignInValidationId4')));
			return;
		}

		toast.success(t('SignUpRegistrationSuccess'));
		resetForm();
		navigate('/sign-in');
	};

	return (
		<div className={styles.card}>
			<h2 className={styles.cardTitle}>{t('AuthId3')}</h2>

			<Formik
				initialValues={{
					full_name: '',
					email: '',
					password: '',
					confirmPassword: '',
					agreeToTerms: false,
				}}
				validationSchema={signUpSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, touched, status, isSubmitting }) => (
					<Form className={styles.form} noValidate>
						<div className={styles.inputGroup}>
							<label htmlFor='full_name' className={styles.label}>
								{t('SignUpId3')}
							</label>
							<Field
								id='full_name'
								name='full_name'
								type='text'
								autoComplete='name'
								placeholder={t('SignUpId4')}
								className={`${styles.input} ${
									touched.full_name && errors.full_name ? styles.inputError : ''
								}`}
							/>
							<FieldError name='full_name' t={t} />
						</div>

						<div className={styles.inputGroup}>
							<label htmlFor='email' className={styles.label}>
								{t('SignUpId5')}
							</label>
							<Field
								id='email'
								name='email'
								type='email'
								autoComplete='email'
								inputMode='email'
								placeholder={t('SignUpId6')}
								className={`${styles.input} ${
									touched.email && errors.email ? styles.inputError : ''
								}`}
							/>
							<FieldError name='email' t={t} />
						</div>

						<FieldPassword
							id='password'
							label={t('SignUpId9')}
							placeholder={t('SignUpId10')}
							hasError={touched.password && !!errors.password}
							eyeClassName={styles.eyeBox}
							t={t}
						/>

						<FieldPassword
							id='confirmPassword'
							label={t('SignUpId11')}
							placeholder={t('SignUpId12')}
							hasError={touched.confirmPassword && !!errors.confirmPassword}
							eyeClassName={styles.eyeBoxConfirm}
							t={t}
						/>

						<div className={styles.checkboxGroup}>
							<Field
								id='agreeToTerms'
								name='agreeToTerms'
								type='checkbox'
								className={styles.checkbox}
							/>
							<p className={styles.checkboxLabel}>
								<label htmlFor='agreeToTerms'>{t('SignUpId13')}</label>{' '}
								<Link
									to='/terms'
									className={styles.policyLink}
									target='_blank'
									rel='noreferrer'
								>
									{t('SignUpId14')}
								</Link>{' '}
								{t('SignUpId15')}{' '}
								<Link
									to='/privacy'
									className={styles.policyLink}
									target='_blank'
									rel='noreferrer'
								>
									{t('SignUpId16')}
								</Link>
							</p>
						</div>
						<FieldError name='agreeToTerms' t={t} />

						{status && <p className={styles.errorMessage}>{status}</p>}

						<Button type='submit' fullWidth loading={isSubmitting}>
							{isSubmitting ? t('SignUpId17') : t('SignUpId18')}
						</Button>

						<div className={styles.linelBlock}>
							<div></div>
							<p>{intl.formatMessage({ id: 'SignInId11' })}</p>
							<div></div>
						</div>

						<FirebaseGoogleSignIn onMfaRequired={mfa.open} />
					</Form>
				)}
			</Formik>

			<div className={styles.footer}>
				<p className={styles.footerText}>
					{t('SignUpId20')}{' '}
					<Link to='/sign-in' className={styles.link}>
						{t('SignUpId21')}
					</Link>
				</p>
			</div>

			{mfa.mfaData && <MfaModal mfa={mfa} />}
		</div>
	);
}
